import { useResetPylon } from "@/hooks/use-boot-in-app-chat";
import { useGitpodAPI } from "@/hooks/use-gitpod-api";
import { useResetSegment } from "@/hooks/use-segment";
import { getPrincipal } from "@/principal";
import { getAccountQueryKey, useGetAccount } from "@/queries/account-queries";
import { isNotFoundError, isUnauthorizedError } from "@/queries/errors";
import { organizationQueryKey } from "@/queries/organization-queries";
import { keyWithPrincipal } from "@/queries/principal-key";
import { toPlainMessage, type PlainMessage } from "@bufbuild/protobuf";
import { Code, ConnectError } from "@connectrpc/connect";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { DotfilesConfiguration, type User } from "gitpod-next-api/gitpod/v1/user_pb";

export type PlainUser = PlainMessage<User>;

export const keys = {
    getAuthenticatedUserQueryKey: () => keyWithPrincipal(["user", "authenticated"]),
    getDotfilesConfigurationQueryKey: () => keyWithPrincipal(["user", "dotfilesConfiguration"]),
};

export const useAuthenticatedUser = () => {
    const api = useGitpodAPI();
    const { data: account } = useGetAccount();

    const query = useQuery({
        queryKey: keys.getAuthenticatedUserQueryKey(),
        enabled: !!account && !!getPrincipal(),
        queryFn: async () => {
            try {
                const { user } = await api.userService.getAuthenticatedUser(
                    {},
                    {
                        timeoutMs: 1000 * 30,
                    },
                );

                if (!user) {
                    throw new Error("User not found");
                }
                return toPlainMessage(user);
            } catch (error) {
                if (isUnauthorizedError(error)) {
                    // TODO(at) implicit rerender into create/join org selector
                    return undefined;
                }
                if (isNotFoundError(error)) {
                    // TODO(at) implicit rerender into create/join org selector
                    return undefined;
                }

                // Otherwise throw like normal
                throw error;
            }
        },
        retry(_failureCount, error) {
            // Don't bother retrying if it's an unauthenticated error
            if (isUnauthorizedError(error)) {
                return false;
            }
            if (_failureCount > 2) {
                return false;
            }

            return true;
        },
        refetchInterval(query) {
            // Don't refetch if unauthenticated
            if (isUnauthorizedError(query.state.error) || query.state.data === null) {
                return false;
            }

            // TODO(at) decide if this is still necessary

            // otherwise refetch every minute
            return 1000 * 60;
        },
        refetchOnWindowFocus: true,
        staleTime: 1_000 * 60 * 5, // 5 minutes
        gcTime: 1_000 * 60 * 5, // 5 minutes
    });
    return query;
};

export const useLogout = () => {
    const { data: account } = useGetAccount();
    const client = useQueryClient();
    const resetMixpanel = useResetSegment();
    const resetPylon = useResetPylon();

    return useMutation({
        mutationFn: async () => {
            if (!account) {
                throw new Error("Not authenticated");
            }

            const resp = await fetch("/auth/oidc/logout", {
                method: "POST",
                credentials: "same-origin",
            });

            return resp;
        },
        onSuccess: () => {
            client.clear();
            resetMixpanel();
            resetPylon();
        },
    });
};

export const useSuspendUser = () => {
    const api = useGitpodAPI();
    const { data: user } = useAuthenticatedUser();
    const client = useQueryClient();

    return useMutation({
        mutationFn: async ({ userId, suspended }: { userId: string; suspended: boolean }) => {
            return await api.userService.setSuspended({
                userId,
                suspended,
            });
        },
        onSuccess: async (_, { userId }) => {
            if (!user) {
                return;
            }

            if (user.id === userId) {
                // We've just suspended ourselves
                await client.invalidateQueries({
                    queryKey: getAccountQueryKey(),
                });
            } else {
                // Invalidate the individual member cache so DataLoader-backed observers refetch
                await client.invalidateQueries({
                    queryKey: organizationQueryKey.member(userId),
                });
                // Suspended/unsuspended another user, refresh the members list
                await client.invalidateQueries({
                    queryKey: organizationQueryKey.listMembers(user.organizationId || "").slice(0, -1),
                });
                // Invalidate all listMembersPaginated queries (with any filter/sort/page)
                await client.invalidateQueries({
                    queryKey: organizationQueryKey.listMembersPaginated(user.organizationId || "").slice(0, -1),
                });
                // Invalidate member count queries
                await client.invalidateQueries({
                    queryKey: organizationQueryKey.membersCount(user.organizationId || "").slice(0, -1),
                });
            }
        },
    });
};

export const useDeleteUser = () => {
    const api = useGitpodAPI();
    const { data: user } = useAuthenticatedUser();
    const client = useQueryClient();

    return useMutation({
        mutationFn: async ({ userId }: { userId: string }) => {
            return await api.userService.deleteUser({
                userId,
            });
        },
        onSuccess: async (_, { userId }) => {
            if (!user) {
                return;
            }

            if (user.id === userId) {
                // We've just deleted ourselves from the org
                await client.invalidateQueries({
                    queryKey: getAccountQueryKey(),
                });
            } else {
                // Remove the individual member cache entry (user no longer exists)
                client.removeQueries({
                    queryKey: organizationQueryKey.member(userId),
                });
                // Deleted another user, refresh the members list
                await client.invalidateQueries({
                    queryKey: organizationQueryKey.listMembers(user.organizationId || "").slice(0, -1),
                });
                // Invalidate all listMembersPaginated queries (with any filter/sort/page)
                await client.invalidateQueries({
                    queryKey: organizationQueryKey.listMembersPaginated(user.organizationId || "").slice(0, -1),
                });
                // Invalidate member count queries
                await client.invalidateQueries({
                    queryKey: organizationQueryKey.membersCount(user.organizationId || "").slice(0, -1),
                });
            }
        },
    });
};

export const useGetDotfilesConfiguration = () => {
    const api = useGitpodAPI();
    return useQuery({
        queryKey: keys.getDotfilesConfigurationQueryKey(),
        queryFn: async () => {
            try {
                const response = await api.userService.getDotfilesConfiguration({});
                return response.dotfilesConfiguration;
            } catch (error) {
                // If the dotfiles configuration is not set we'll get a 404 which we'll treat as "no configuration"
                const isNotFound = error instanceof ConnectError && error.code === Code.NotFound;
                if (isNotFound) {
                    return new DotfilesConfiguration({ repository: "" });
                }
                throw error;
            }
        },
    });
};

export const useSetDotfilesConfiguration = () => {
    const api = useGitpodAPI();
    const client = useQueryClient();
    return useMutation({
        mutationFn: async ({ repositoryUrl }: { repositoryUrl: string }) => {
            return await api.userService.setDotfilesConfiguration({
                repository: repositoryUrl,
            });
        },
        onSuccess: async (_, { repositoryUrl }) => {
            await client.setQueryData(
                keys.getDotfilesConfigurationQueryKey(),
                new DotfilesConfiguration({ repository: repositoryUrl }),
            );
        },
    });
};
